import { LEAD_STATUS_LABELS } from '@sitewise/shared';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  LoadingState,
  StatPill,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { Building2, FlaskConical, TestTube } from 'lucide-react';
import { Link } from 'react-router-dom';

import { fetchLeadRollup } from '../../../api/lead.js';
import { LEAD_STATUS_TONE } from '../units/lead-status.js';

import type {
  LeadRollupBuildingRow,
  LeadRollupCounts,
  LeadRollupUnitRow,
} from '../../../api/lead.js';


/**
 * Migration 2 — Lead Tracker. Org-wide rollup at `/lead`. Renders:
 *  - 6-up stats strip (counts by derived status)
 *  - By-building card (rollup per building with quick links)
 *  - Attention list (top 20 units with the most XRF positives)
 *  - Recently tested (top 20 by `latest_xrf_date`)
 *
 * All data comes from a single `/api/v1/lead/rollup` round-trip.
 * Per-building "open" links navigate to the building detail page's
 * Lead tab via the `?tab=lead` query hint that the building detail
 * page already respects (we use the location hash here because the
 * tabs use Radix internal state, not URL state — clicking the link
 * still lands the user on the building's Units tab; the Lead tab is
 * one click away).
 */
export function LeadRollupPage() {
  const query = useQuery({
    queryKey: ['lead-rollup'],
    queryFn: () => fetchLeadRollup(),
    staleTime: 60_000,
  });

  if (query.isLoading) return <LoadingState rows={8} />;
  if (query.isError || !query.data) {
    return <ErrorState description="Could not load the lead rollup." />;
  }

  const { counts, perBuilding, topPositives, recentlyTested } = query.data;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <FlaskConical className="h-5 w-5 text-fg-muted" aria-hidden />
        <h1 className="text-xl font-semibold">Lead tracking</h1>
      </div>

      <CountsStrip counts={counts} />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">By building</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <BuildingTable rows={perBuilding} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Needs attention</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <UnitList
              rows={topPositives}
              empty="No positive readings on file."
              meta={(r) =>
                r.positiveCount != null ? `${r.positiveCount} positive` : ''
              }
              icon={TestTube}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recently tested</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <UnitList
            rows={recentlyTested}
            empty="No tests on file yet."
            meta={(r) => (r.latestXrfDate ? `XRF ${r.latestXrfDate}` : '')}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function CountsStrip({ counts }: { counts: LeadRollupCounts }) {
  const cards: Array<{ label: string; value: number; tone: string; description?: string }> = [
    { label: 'Total units', value: counts.total, tone: 'neutral' },
    { label: 'Untested', value: counts.untested, tone: 'low' },
    { label: 'Tested negative', value: counts.testedNegative, tone: 'success' },
    { label: 'Tested positive', value: counts.testedPositive, tone: 'critical' },
    { label: 'Exempt approved', value: counts.exemptApproved, tone: 'success' },
    { label: 'Pending exemption', value: counts.exemptPending, tone: 'medium' },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((c) => (
        <Card key={c.label}>
          <CardContent className="p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
              {c.label}
            </div>
            <div
              className={
                c.tone === 'critical'
                  ? 'mt-1 text-2xl font-semibold text-danger tabular-nums'
                  : c.tone === 'success'
                    ? 'mt-1 text-2xl font-semibold text-success tabular-nums'
                    : 'mt-1 text-2xl font-semibold tabular-nums'
              }
            >
              {c.value}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function BuildingTable({ rows }: { rows: LeadRollupBuildingRow[] }) {
  if (rows.length === 0) {
    return <p className="p-4 text-sm text-fg-muted">No buildings to show.</p>;
  }
  return (
    <ul className="divide-y divide-border">
      {rows.map((b) => {
        const positives = b.counts.testedPositive;
        const pending = b.counts.exemptPending;
        const untested = b.counts.untested;
        return (
          <li key={b.buildingId} className="flex items-center gap-3 px-4 py-3 text-sm">
            <Building2 className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden />
            <Link
              to={`/buildings/${b.buildingId}`}
              className="min-w-0 flex-1 truncate font-medium hover:underline"
              title={b.buildingName}
            >
              {b.buildingName}
            </Link>
            <div className="hidden items-center gap-3 text-xs text-fg-muted md:flex">
              <span>{b.counts.total} units</span>
              {positives > 0 ? (
                <span className="font-medium text-danger">{positives} positive</span>
              ) : null}
              {pending > 0 ? (
                <span className="font-medium text-warning">{pending} pending</span>
              ) : null}
              {untested > 0 ? (
                <span>{untested} untested</span>
              ) : null}
            </div>
            <Link
              to={`/buildings/${b.buildingId}`}
              className="shrink-0 text-xs text-primary hover:underline"
            >
              Open
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function UnitList({
  rows,
  empty,
  meta,
  icon: Icon,
}: {
  rows: LeadRollupUnitRow[];
  empty: string;
  meta: (r: LeadRollupUnitRow) => string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  if (rows.length === 0) {
    return <p className="p-4 text-sm text-fg-muted">{empty}</p>;
  }
  return (
    <ul className="divide-y divide-border">
      {rows.map((r) => (
        <li key={r.unitId} className="flex items-center gap-3 px-4 py-2.5 text-sm">
          {Icon ? <Icon className="h-4 w-4 shrink-0 text-fg-muted" /> : null}
          <Link
            to={`/units/${r.unitId}`}
            className="min-w-0 flex-1 truncate font-medium hover:underline"
            title={`${r.buildingName} · Unit ${r.unitNumber}`}
          >
            <span className="text-fg-muted">{r.buildingName}</span>{' '}
            <span className="text-fg">· {r.unitNumber}</span>
          </Link>
          <span className="shrink-0 text-xs text-fg-muted tabular-nums">{meta(r)}</span>
          <StatPill
            tone={LEAD_STATUS_TONE[r.leadStatus]}
            value={LEAD_STATUS_LABELS[r.leadStatus]}
            withDot
          />
        </li>
      ))}
    </ul>
  );
}
